import React, { useState, useEffect } from 'react';
import { Box, List, ListItem, ListItemButton, ListItemText, Typography, IconButton, Avatar, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

export default function ChatList({ conversations, selectedId, onSelect, onRefresh }) {
  const [profilePictures, setProfilePictures] = useState({});
  const [loadingPictures, setLoadingPictures] = useState(false);

  // Fetch profile pictures for all peers
  useEffect(() => {
    const fetchProfilePictures = async () => {
      if (!conversations || conversations.length === 0) return;
      
      setLoadingPictures(true);
      const pictures = {};
      
      for (const conv of conversations) {
        const peerId = conv.peer?.id || conv.peer?._id;
        if (peerId && !pictures[peerId]) {
          try {
            const token = localStorage.getItem('access_token') || localStorage.getItem('rv_access_token');
            const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
            
            const response = await fetch(`${API_BASE}/api/profile/user/${peerId}/picture-url`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });

            if (response.ok) {
              const data = await response.json();
              if (data.url) {
                pictures[peerId] = data.url;
              }
            }
          } catch (error) {
            console.error(`Failed to fetch profile picture for user ${peerId}:`, error);
          }
        }
      }
      
      setProfilePictures(pictures);
      setLoadingPictures(false);
    };

    fetchProfilePictures();
  }, [conversations]);

  const getAvatarLetter = (peer) => {
    const name = peer?.name || peer?.username || 'U';
    return name[0].toUpperCase();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <Typography variant="subtitle2" fontWeight="bold">
          {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
        </Typography>
        <IconButton size="small" onClick={onRefresh}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
        {conversations.map(conv => {
          const peerId = conv.peer?.id || conv.peer?._id;
          const profilePicUrl = profilePictures[peerId];
          
          return (
            <ListItem key={conv.id} disablePadding>
              <ListItemButton
                selected={conv.id === selectedId}
                onClick={() => onSelect(conv)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                  },
                }}
              >
                <Avatar
                  src={profilePicUrl || undefined}
                  sx={{
                    width: 40,
                    height: 40,
                    mr: 2,
                    bgcolor: '#667eea',
                  }}
                >
                  {!profilePicUrl && getAvatarLetter(conv.peer)}
                </Avatar>
                <ListItemText
                  primary={conv.peer?.name || conv.peer?.username || 'Unknown'}
                  secondary={conv.lastMessage?.text || 'No messages yet'}
                  secondaryTypographyProps={{
                    noWrap: true,
                    sx: { maxWidth: '180px' }
                  }}
                />
                {conv.lastMessage?.createdAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {new Date(conv.lastMessage.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
